import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  Home, Users, FileBadge, Inbox, Settings, Plus, UserPlus, FileCheck2,
  QrCode, ShieldCheck, BarChart3, Mail, X, GraduationCap,
} from "lucide-react";
import QRScanner from "@/components/QRScanner";

const ORG_TABS = [
  { to: "/dashboard", label: "Home", icon: Home, testid: "mob-tab-home" },
  { to: "/workspace/employees", label: "Team", icon: Users, testid: "mob-tab-team" },
  { to: "/workspace/approvals", label: "Inbox", icon: Inbox, testid: "mob-tab-inbox" },
  { to: "/certificates", label: "Certs", icon: FileBadge, testid: "mob-tab-certs" },
];
const INST_TABS = [
  { to: "/dashboard", label: "Home", icon: Home, testid: "mob-tab-home" },
  { to: "/workspace/students", label: "Students", icon: GraduationCap, testid: "mob-tab-team" },
  { to: "/workspace/approvals", label: "Inbox", icon: Inbox, testid: "mob-tab-inbox" },
  { to: "/certificates", label: "Certs", icon: FileBadge, testid: "mob-tab-certs" },
];
const IND_TABS = [
  { to: "/dashboard", label: "Home", icon: Home, testid: "mob-tab-home" },
  { to: "/passport", label: "Passport", icon: ShieldCheck, testid: "mob-tab-passport" },
  { to: "/my-records", label: "Records", icon: FileBadge, testid: "mob-tab-records" },
  { to: "/certificates", label: "Certs", icon: FileBadge, testid: "mob-tab-certs" },
];

const ORG_FAB_ACTIONS = [
  { to: "/workspace/employees/add", label: "Add Employee", icon: UserPlus, testid: "fab-add-employee" },
  { to: "/workspace/certificates/issue", label: "Issue Certificate", icon: FileCheck2, testid: "fab-issue-cert" },
  { action: "scan-qr", label: "Scan QR", icon: QrCode, testid: "fab-scan-qr" },
  { to: "/verify", label: "Verify Certificate", icon: ShieldCheck, testid: "fab-verify" },
  { to: "/hr-dashboard", label: "Reports", icon: BarChart3, testid: "fab-reports" },
];
const INST_FAB_ACTIONS = [
  { to: "/workspace/students/add", label: "Add Student", icon: UserPlus, testid: "fab-add-student" },
  { to: "/workspace/certificates/issue", label: "Issue Certificate", icon: FileCheck2, testid: "fab-issue-cert" },
  { action: "scan-qr", label: "Scan QR", icon: QrCode, testid: "fab-scan-qr" },
  { to: "/verify", label: "Verify Certificate", icon: ShieldCheck, testid: "fab-verify" },
];
const IND_FAB_ACTIONS = [
  { to: "/passport", label: "My Passport", icon: ShieldCheck, testid: "fab-passport" },
  { to: "/verify", label: "Verify Certificate", icon: ShieldCheck, testid: "fab-verify" },
  { action: "scan-qr", label: "Scan QR", icon: QrCode, testid: "fab-scan-qr" },
];

export default function MobileBottomNav() {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [scan, setScan] = useState(false);

  if (!user) return null; // hide on landing/login

  const role = user.role;
  const isOrg = role === "organization" || role === "super_admin" || role === "verifier";
  const isInst = role === "institution";
  const tabs = isOrg ? ORG_TABS : isInst ? INST_TABS : IND_TABS;
  const actions = isOrg ? ORG_FAB_ACTIONS : isInst ? INST_FAB_ACTIONS : IND_FAB_ACTIONS;

  const handleQR = (decoded) => {
    setScan(false);
    setOpen(false);
    const m = decoded.match(/(EC-[A-Z0-9-]+)/i);
    const id = m ? m[1].toUpperCase() : decoded.trim();
    window.location.href = `/verify/${encodeURIComponent(id)}`;
  };

  return (
    <>
      {/* FAB sheet overlay */}
      {open && (
        <div className="fixed inset-0 bg-black/50 z-40 md:hidden flex items-end" onClick={() => setOpen(false)} data-testid="fab-sheet">
          <div className="w-full bg-white rounded-t-2xl p-5 pb-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div className="eyebrow">Quick actions</div>
              <button onClick={() => setOpen(false)} data-testid="fab-sheet-close"><X className="w-5 h-5 text-ec-ink-soft" /></button>
            </div>
            <div className="grid grid-cols-3 gap-3" data-testid="fab-actions">
              {actions.map((a) => {
                const Icon = a.icon;
                const content = (
                  <div className="flex flex-col items-center gap-1.5 p-3 border border-ec-line rounded-sm hover:bg-ec-mute active:scale-95 transition" data-testid={a.testid}>
                    <Icon className="w-6 h-6 text-ec-emerald" />
                    <span className="text-[11px] text-center text-ec-ink">{a.label}</span>
                  </div>
                );
                if (a.action === "scan-qr") {
                  return <button key={a.label} onClick={() => setScan(true)}>{content}</button>;
                }
                return <Link key={a.label} to={a.to} onClick={() => setOpen(false)}>{content}</Link>;
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bottom nav bar (mobile only) */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-ec-line md:hidden" data-testid="mobile-bottom-nav">
        <div className="relative grid grid-cols-5 h-14">
          {tabs.slice(0, 2).map((t) => <BottomTab key={t.to} t={t} loc={loc} />)}
          <button onClick={() => setOpen(true)} className="relative flex items-center justify-center" data-testid="mobile-fab">
            <span className="absolute -top-6 w-14 h-14 rounded-full bg-ec-emerald text-ec-ink flex items-center justify-center shadow-lg active:scale-95 transition">
              <Plus className="w-6 h-6" />
            </span>
          </button>
          {tabs.slice(2, 4).map((t) => <BottomTab key={t.to} t={t} loc={loc} />)}
        </div>
      </nav>

      {/* Spacer so content isn't hidden behind nav */}
      <div className="h-16 md:hidden" />

      {scan && <QRScanner onScan={handleQR} onClose={() => setScan(false)} />}
    </>
  );
}

function BottomTab({ t, loc }) {
  const Icon = t.icon;
  const active = loc.pathname === t.to || loc.pathname.startsWith(t.to + "/");
  return (
    <Link to={t.to} className={`flex flex-col items-center justify-center text-[10px] gap-0.5 ${active ? "text-ec-emerald" : "text-ec-ink-soft"}`} data-testid={t.testid}>
      <Icon className="w-5 h-5" />
      {t.label}
    </Link>
  );
}
